import React, { useState, useEffect, memo } from 'react';
import { get, isNil, isString, size } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import { FormControlLabel } from '@mui/material';

import Loader from '../../../../common/components/Loader/Loader';
import { Dialog } from '../../../../common/components/Dialog/Dialog';
import Button from '../../../../common/components/Button/Button';
import TextField from '../../../../common/components/TextField/TextField';
import { sortingLowerCase } from '../../../../common/services/I2P2Helper';
import Checkbox from '../../../../common/components/Checkbox/Checkbox';
import { NoReleaseNotesMessage } from '../../../MultiFwUpdate/components/Toolbar/components/Autocomplete/ReleaseNotesTooltip';
import Autocomplete from '../../../../common/components/Autocomplete/Autocomplete';

const loaderContainerStyle = {
  position: 'relative',
  padding: 25,
  margin: '30px 80px',
};

const forceUpdateStyle = {
  marginTop: '29px',
  marginBottom: '20px',
  marginLeft: 'unset',
};

const AutocompleteInputStyle = {
  margin: '0 0 20px',
};

function FirmwareUpdateDialog({
  loadUpdateFiles,
  modalOpen,
  setForceFWUpdate,
  onCancel,
  forceUpdate,
  firmwareUpdateWithFile,
  c_uuid,
  c_online,
  selectedFileId,
  filesList,
  isFetching,
  error,
  selectFile,
}) {
  const [prevModalOpen, setPrevModalOpen] = useState(false);

  useEffect(() => {
    if (prevModalOpen !== modalOpen && modalOpen) {
      loadUpdateFiles();
    }

    setPrevModalOpen(modalOpen);
  }, [modalOpen]);

  const onSubmit = (activeFile) => {
    activeFile.force_update = forceUpdate;
    firmwareUpdateWithFile(activeFile, c_uuid, c_online);
  };

  const handleForceUpdateChange = (event, value) => {
    setForceFWUpdate(value);
  };

  const processFileDescription = (activeFile) => {
    let description = get(activeFile, 'packages');

    if (isString(description)) {
      description = description
        .split(';')
        .map((item) => item.trim())
        .join(';\n');
      if (description.endsWith(';\n')) {
        description = description.substring(0, description.length - 1);
      }
    }

    return description || '';
  };

  const activeFile = filesList.find((file) => file.uuid === selectedFileId);
  const description = processFileDescription(activeFile);
  const releaseNotes = get(activeFile, 'releaseNotes');

  const actions = [
    <Button key={uuidv4()} disabled={isFetching} onClick={onCancel}>
      Cancel
    </Button>,
    <Button key={uuidv4()} disabled={isNil(activeFile) || isFetching} onClick={() => onSubmit(activeFile)}>
      Update
    </Button>,
  ];

  const loader = (
    <div style={loaderContainerStyle}>
      <Loader />
    </div>
  );

  const sortedList = filesList.sort((a, b) => sortingLowerCase(a, b, 'firmwareVersionName'));

  const menu = (
    <>
      <Autocomplete
        key="fw-list"
        options={sortedList}
        getOptionLabel={(option) => option.firmwareVersionName}
        onChange={(event, newValue) => {
          if (newValue) {
            selectFile(newValue.uuid);
          } else {
            selectFile('');
          }
        }}
        renderInput={(params) => (
          <TextField label="FW Update Name" fullWidth style={AutocompleteInputStyle} {...params} InputLabelProps={{ shrink: true }} />
        )}
      />

      <TextField
        fullWidth
        label="Description"
        disabled
        multiline
        value={description}
        minRows={1}
        maxRows={20}
        style={{ color: 'rgba(0, 0, 0, 0.38)!important' }}
      />

      <TextField
        fullWidth
        label="Release Notes"
        disabled
        multiline
        value={releaseNotes || ''}
        placeholder={activeFile ? NoReleaseNotesMessage : ''}
        minRows={1}
        maxRows={20}
        style={{ color: 'rgba(0, 0, 0, 0.38)!important', marginTop: 20 }}
      />

      <FormControlLabel
        control={<Checkbox checked={forceUpdate} onChange={handleForceUpdateChange} />}
        style={forceUpdateStyle}
        label="Force Update"
      />
      {forceUpdate && (
        <p className="gray-note">
          FW Update will be sent to the device in spite of any restrictive validation rules only if the device is not in factory boot mode.
        </p>
      )}

      {size(error) > 0 && <div className="error">{error}</div>}
      <p className="red-note">
        FW Update might take a couple of minutes. During this process charger will be displayed as Offline and will change its status to
        Online after the update is completed. As a reminder, you can always see latest FW Version under the Details tab.
      </p>
    </>
  );

  return (
    <Dialog actions={actions} title="FW Update" open={modalOpen} modal onClose={onCancel}>
      {isFetching ? loader : menu}
    </Dialog>
  );
}

export default memo(FirmwareUpdateDialog);
